import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaUno(props, ref) {
  const { title, observations, logoSrc, leftColor, rightColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    // prepare cached Image used by draw()
    imgRef.current = new Image();
    imgRef.current.crossOrigin = 'anonymous';
  }, []);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve();
      const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      // reset transform and scale once to avoid double-scaling on high-DPI displays
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      const leftW = Math.floor(width * 0.8);
      const rightW = width - leftW;

      ctx.fillStyle = opts.leftColor || leftColor || '#3c8dbc';
      ctx.fillRect(0, 0, leftW, height);

      ctx.fillStyle = opts.rightColor || rightColor || '#b9bcc0ff';
      ctx.fillRect(leftW, 0, rightW, height);

      const padding = 120;
      ctx.fillStyle = '#ffffff';

      /// title size adjustable and not double-scaled
      const titleFontSize = opts.titleFontSize || 90;
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textBaseline = 'top';
      const t = opts.title || title || 'Desplazamiento Forzado';
      ctx.fillText(t, padding, padding + 150);

      // subtitle (right under title)
      const subTitleFontSize = opts.subTitleFontSize || 90;
      ctx.font = `bold ${subTitleFontSize}px Inter, Arial, sans-serif`;
      const subTitle = opts.subTitle || 'en Medellín';
      const subTitleY = padding + titleFontSize + 158;
      ctx.fillText(subTitle, padding, subTitleY);

      // TituloObservacion (right under subtitle)
      const tituloObservacionFontSize = opts.tituloObservacionFontSize || 30;
      ctx.font = `italic ${tituloObservacionFontSize}px Inter, Arial, sans-serif`;
      const tituloObservacion = props.TituloObservacion || '';
      const tituloObservacionY = subTitleY + subTitleFontSize + 20;
      ctx.fillText(tituloObservacion, padding, tituloObservacionY);

      // observations start below tituloObservacion
      const obsStartY = tituloObservacionY + tituloObservacionFontSize + 60;

      const obsText = opts.observations ||
        observations ||
        'Construido con base en los registros del Equipo de Atención y Reparación a Víctimas (EARV), referentes a la atención de personas que declaran hechos victimizantes ante la Personería, Defensoría o Procuraduría, ocurridos en los últimos tres meses anteriores a la atención, según lo ordenado por la Ley 1448 de 2011, artículo 47, modificado por la Ley 2421 de 2024.';

      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      // observation text smaller and positioned under the subtitle, with tabulation
      ctx.font = '35px Inter, Arial, sans-serif';
      const maxTextW = leftW - padding * 2;
      const lineHeight = 50;
      const textX = padding + 40; // tabulation/indent

      const wrapText = (context, text, x, y, maxWidth, lineH) => {
        const words = text.split(' ');
        let line = '';
        let lineWords = [];
        
        for (let n = 0; n < words.length; n++) {
          const testLine = line ? line + ' ' + words[n] : words[n];
          const metrics = context.measureText(testLine);
          if (metrics.width > maxWidth && line) {
            // Justify this line (except if it's the last line)
            const isLastLine = n === words.length - 1;
            if (!isLastLine && lineWords.length > 1) {
              const lineText = lineWords.join(' ');
              const textWidth = context.measureText(lineText).width;
              const extraSpace = maxWidth - textWidth;
              const gaps = lineWords.length - 1;
              const spacePerGap = extraSpace / gaps;
              
              let currentX = x;
              for (let i = 0; i < lineWords.length; i++) {
                context.fillText(lineWords[i], currentX, y);
                const wordWidth = context.measureText(lineWords[i]).width;
                currentX += wordWidth + context.measureText(' ').width + spacePerGap;
              }
            } else {
              context.fillText(line, x, y);
            }
            
            line = words[n];
            lineWords = [words[n]];
            y += lineH;
          } else {
            line = testLine;
            if (line === words[n]) {
              lineWords = [words[n]];
            } else {
              lineWords.push(words[n]);
            }
          }
        }
        // Last line is not justified
        if (line) context.fillText(line, x, y);
        return y + lineH;
      };

      const obsString = Array.isArray(obsText) ? obsText.join(' ') : String(obsText);
      wrapText(ctx, obsString, textX, obsStartY, maxTextW - 12 /* account for indent */, lineHeight);

      const footer = 'Secretaría de Paz y Derechos Humanos';
      ctx.font = 'bold 30px Inter, Arial, sans-serif';
      const footerMetrics = ctx.measureText(footer);
      const footerX = (leftW - footerMetrics.width) / 2;
      const footerY = height - padding - 24;
      ctx.fillText(footer, footerX, footerY);


      ctx.strokeStyle = 'rgba(0,0,0,0.06)';
      ctx.beginPath();
      ctx.moveTo(leftW + 0.5, 8);
      ctx.lineTo(leftW + 0.5, height - 8);
      ctx.stroke();

      // example: when creating logo image
      const img = imgRef.current || new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const maxLogoW = rightW - 40;
          const maxLogoH = height - 40;
          let lw = img.width;
          let lh = img.height;
          const ratio = Math.min(maxLogoW / lw, maxLogoH / lh, 1);
          lw = lw * ratio;
          lh = lh * ratio;
          const lx = leftW + (rightW - lw) / 2;
          const ly = (height - lh) / 2;
          ctx.drawImage(img, lx, ly, lw, lh);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('getPaginaUno: drawImage failed', e);
        }
        resolve();
      };
      img.onerror = (err) => {
        // fallback placeholder and resolve
        ctx.fillStyle = '#9ca3af';
        const boxW = Math.min(120, rightW - 40);
        const boxH = 60;
        const bx = leftW + (rightW - boxW) / 2;
        const by = (height - boxH) / 2;
        ctx.fillRect(bx, by, boxW, boxH);
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Inter, Arial, sans-serif';
        ctx.fillText('LOGO', bx + 12, by + 20);
        // eslint-disable-next-line no-console
        console.error('getPaginaUno: logo load error', opts.logoSrc || logoSrc);
        resolve();
      };
      const src = opts.logoSrc || logoSrc;
      if (img.src !== src) img.src = src;
      // if no image to load (src empty) resolve immediately:
      if (!src) resolve();
    });
  };

  useImperativeHandle(ref, () => ({
    draw,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    // initial draw
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});